import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
    Sidebar,
    SidebarTab,
    WarningModal,
    NotificationSystem,
    SubItem,
    updateTabEvents,
    SidebarMenuMapType,
    EmailConfirmationNotification,
} from 'components';
import { Location } from 'history';
import { push } from 'connected-react-router';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { useShowGlobalErrorModal } from 'hooks';
import QecIcon from 'components/assets/svg/sidebar/cok.svg';
import ExamIcon from 'components/assets/svg/sidebar/exams.svg';
import QualificationIcon from 'components/assets/svg/sidebar/qualif.svg';
import ExpertsIcon from 'components/assets/svg/sidebar/experts.svg';

const TABS: SidebarTab[] = [
    {
        name: 'Экзамены',
        icon: ExamIcon,
        state: 'exams',
        list: [
            {
                name: 'Запросы на участие',
                path: '/participation-requests',
                routes: ['/exam-invite/*'],
            },
            {
                name: 'Проведение экзаменов',
                path: '/exam-timetables',
                routes: ['/exam-timetables', '/exam/*?from=exam-timetables', '/exam-application-details/*'],
            },
            {
                name: 'Обработка экзаменов',
                path: '/exam-in-progress',
                routes: ['/exam-in-progress', '/exam/*?from=exam-in-progress', '/application-exam-details/*'],
            },
            {
                name: 'Список экзаменов',
                path: '/exam-list',
                routes: ['/exam-list', '/exam/*?from=exam-list'],
            },
        ],
    },
    {
        name: 'ЭЦ',
        icon: QecIcon,
        state: 'qec',
        list: [
            {
                name: 'Связанные ЭЦ',
                path: '/connected-ec',
            },
        ],
    },
    {
        name: 'Квалификации',
        icon: QualificationIcon,
        state: 'qualifications',
        list: [
            {
                name: 'Мои квалификации',
                path: '/expert-qualifications',
                routes: ['/qualification-detail/*'],
            },
            {
                name: 'Свидетельство эксперта',
                path: '/expert-certificate',
            },
        ],
    },
];

const registrationCompletionTab = {
    name: 'Регистрация',
    icon: ExpertsIcon,
    state: 'registration',
    list: [
        {
            name: 'Завершение регистрации',
            path: '/registration-completion',
        },
    ],
};

const PROFILE_TABS = [
    {
        path: '/my-profile',
        name: 'Настройки профиля',
        mini: 'НП',
    },
];

const SidebarContainer = ({
    onFetchProfile,
    startEventStatePoll,
    stopEventStatePoll,
    events,
    errors,
    auth,
    location,
    notifications,
    cleanNotifications,
    onPush,
    sendConfirmationEmail,
    onLogout,
}: Props) => {
    const [tabs, setTabs] = useState(TABS);
    const { showModal, toggleModal } = useShowGlobalErrorModal(errors);

    React.useEffect(() => {
        onFetchProfile();
        if (process.env.REACT_APP_LOCAL_RUN_TESTS !== 'true') {
            startEventStatePoll();
        }
        return () => {
            stopEventStatePoll();
        };
    }, []);

    React.useEffect(() => {
        setTabs(TABS.map(t => updateTabEvents(t, events, menuEventsMap)));
    }, [events]);

    const newTabs = React.useMemo(() => {
        let tabsForRender = tabs;

        if (auth && auth.profile) {
            if (auth.profile.sovietMember && tabsForRender[0].list) {
                const sovietProtocolsTab: SubItem = {
                    name: 'Протоколы совета',
                    path: '/soviet-protocols',
                };

                const haveTab = tabsForRender[0].list.find(e => e.path === sovietProtocolsTab.path);

                if (!haveTab) {
                    tabsForRender[0].list.splice(2, 0, sovietProtocolsTab);
                }
            }

            if (!auth.profile.nokMember && tabsForRender[0].list) {
                tabsForRender[0].list = removeTab(tabsForRender[0].list, ['/participation-requests', '/exam-timetables', '/expert-certificate', '/exam-list']);

                if (auth.profile.sovietMember) {
                    tabsForRender[0].list = removeTab(tabsForRender[0].list, ['/exam-in-progress']);
                }

                tabsForRender.splice(1, 3);
            }

            if (!auth.profile.registrationCompleted && !auth.profile.sovietMember) {
                tabsForRender = [];
                tabsForRender.push(registrationCompletionTab);
            }
        }

        return tabsForRender;
    }, [auth]);

    if (!auth) return null;

    return (
        <>
            <Sidebar
              profile={auth.profile}
              location={location}
              profileTabs={PROFILE_TABS}
              onLogout={onLogout}
              tabs={newTabs}
            />
            {errors.error && errors.handler === 'global' && !errors.error.failures
                ? (
                    <WarningModal
                      title={errors.error.message}
                      show={showModal}
                      onHide={() => toggleModal(false)}
                      onAccess={() => toggleModal(false)}
                      accessBtnTittle="Вернуться"
                      titleClassNames="text-danger"
                    >
                        <p>Код ошибки:{errors.error.code}</p>
                        <p>Тип ошибки:{errors.error.type}</p>
                    </WarningModal>
                )
                : null}
            <NotificationSystem
              notifications={notifications}
              cleanNotifications={cleanNotifications}
            />
            <EmailConfirmationNotification
              profile={auth && auth.profile}
              onPush={onPush}
              sendConfirmationEmail={captcha => sendConfirmationEmail({ captcha })}
              cleanNotifications={cleanNotifications}
              apiError={errors.error}
            />
        </>
    );
};

const mapState = (state: IRootState) => ({
    auth: state.auth.auth,
    errors: state.errors,
    notifications: state.notifications,
    events: state.eventsState,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onFetchProfile: dispatch.auth.fetchProfile,
    cleanErrors: dispatch.errors.clean,
    cleanNotifications: dispatch.notifications.clean,
    startEventStatePoll: dispatch.eventsState.startEventStatePoll,
    stopEventStatePoll: dispatch.eventsState.stopEventStatePoll,
    sendConfirmationEmail: dispatch.auth.sendConfirmationEmailAsync,
    onLogout: dispatch.auth.logout,
    onPush: (key: string) => dispatch(push(key)),
});

export default connect(mapState, mapDispatch)(SidebarContainer);

interface SidebarOwnProps {
    location: Location;
}

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>
type Props = connectedProps & SidebarOwnProps;

const menuEventsMap: SidebarMenuMapType = {
    'Запросы на участие': 'ExpertInvitation',
    'Проведение экзаменов': 'ExamTimetable',
    'Обработка экзаменов': 'ExamInProcessing',
};

const removeTab = (list: SubItem[], path: string[]): SubItem[] => {
    const newList = list;

    path.forEach((p) => {
        list.forEach((item, index) => {
            if (item.path === p) {
                newList.splice(index, 1);
            }
        });
    });


    return newList;
};
